// AvatarLabVoicePlayer.tsx
import React, { useState, useRef, useEffect } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  Button,
  Checkbox,
  CircularProgress,
  Slider,
  Switch,
  MenuItem,
  Select,
  SelectChangeEvent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Tooltip,
  IconButton,
  TextField,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import EditIcon from "@mui/icons-material/Edit";
import { THEME_COLORS } from "../../context/constants";
import { useUserContext } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";
import { useProjectContext } from "../../context/ProjectContext";
import {
  Actor,
  CustomPhotoAvatar,
  CustomVoice,
  HeygenVoice,
  TextVoiceSettings,
  ElevenLabsSettings,
} from "../../context/types";
import {
  getCustomVoicesByWorkspace,
  listAllHeygenVoices,
  updateAvatarLabHeygenVoice,
  updateAvatarLabVoiceSettings,
  updateVoiceSettings,
  getElevenLabsVoices,
  elevenLabsFF,
  updateCustomAvatarElevenLabsVoice,
  generateVoicePreview,
} from "../../client/api";
import VoiceSelectionModal from "../VoicePlayer/components/HeygenVoicesDialog";
import ElevenLabsVoicesDialog from "../VoicePlayer/components/ElevenLabsVoicesDialog";
import useStyles from "../VoicePlayer/style";

interface AvatarLabVoicePlayerProps {
  avatar: CustomPhotoAvatar;
  isSelected: boolean;
  onSelect: (avatarId: string, isCheckboxClick?: boolean) => void;
  onDeselect?: (avatarId: string) => void;
  scriptType: "text" | "speech";
  selectedAvatars: string[];
  setAvatarLabActors: React.Dispatch<React.SetStateAction<CustomPhotoAvatar[]>>;
  onSwitchToPhotoAvatar?: (avatar: CustomPhotoAvatar) => void;
  talkingPhotos?: Actor[];
}

const AvatarLabVoicePlayer: React.FC<AvatarLabVoicePlayerProps> = ({
  avatar,
  isSelected,
  onSelect,
  onDeselect,
  scriptType,
  selectedAvatars,
  setAvatarLabActors,
  onSwitchToPhotoAvatar,
  talkingPhotos: talkingPhotosProp,
}) => {
  const classes = useStyles();
  const { currentWorkspace, user } = useUserContext();
  const { addNotification } = useNotification();
  const { talkingPhotos: contextTalkingPhotos, fetchAvatars, currentScript } = useProjectContext();

  // Use prop if available, otherwise fall back to context
  const talkingPhotos = talkingPhotosProp || contextTalkingPhotos;

  // Fetch talking photos if not available
  useEffect(() => {
    const loadTalkingPhotos = async () => {
      if (!talkingPhotos && currentWorkspace && user?.organization?._id) {
        await fetchAvatars(user.organization._id, currentWorkspace._id);
      }
    };
    loadTalkingPhotos();
  }, [talkingPhotos, currentWorkspace, user?.organization?._id, fetchAvatars]);

  // Check if a photo avatar version exists for this custom avatar
  const hasPhotoAvatarVersion = talkingPhotos?.some(
    (photo) =>
      !!avatar.generation_id && photo.preview_image_url?.includes(avatar.generation_id)
  );
  const audioRef = useRef<HTMLAudioElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [heygenVoices, setHeygenVoices] = useState<HeygenVoice[]>([]);
  const [heygenCustomVoices, setHeygenCustomVoices] = useState<CustomVoice[]>([]);
  const [heygenCustomVoicesLoading, setHeygenCustomVoicesLoading] = useState(false);
  const [elevenLabsVoices, setElevenLabsVoices] = useState<any[]>([]);
  const [selectedHeygenVoiceId, setSelectedHeygenVoiceId] = useState<
    string | null
  >(avatar.heygenVoiceId || null);
  const [selectedElevenLabsVoiceId, setSelectedElevenLabsVoiceId] = useState<
    string | null
  >(avatar.elevenLabsVoiceId || null);
  const [heygenVoiceModalOpen, setHeygenVoiceModalOpen] = useState(false);
  const [elevenLabsVoiceModalOpen, setElevenLabsVoiceModalOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(avatar.name || "");
  const [voiceSelection, setVoiceSelection] = useState<"heygen" | "elevenLabs">(
    avatar.elevenLabsVoiceId ? "elevenLabs" : "heygen"
  );
  const [elevenLabsEnabled, setElevenLabsEnabled] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [elevenLabsVoiceSettings, setElevenLabsVoiceSettings] = useState(
    avatar.voiceConfiguration || {
      stability: 0.5,
      similarity_boost: 0.75,
      style: 0.0,
      use_speaker_boost: true,
      speed: 1.0,
    }
  );

  const handleSwitchToPhotoAvatar = () => {
    if (onSwitchToPhotoAvatar && hasPhotoAvatarVersion) {
      onSwitchToPhotoAvatar(avatar);
    }
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error("Error playing audio:", error);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleVoiceSettings = () => {
    setShowSettings(!showSettings);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTitle(e.target.value);
  };

  const handleTitleSave = async () => {
    setIsEditingTitle(false);
    if (currentTitle !== avatar.name) {
      try {
        setAvatarLabActors((prev) =>
          prev.map((a) =>
            a._id === avatar._id ? { ...a, name: currentTitle } : a
          )
        );
      } catch (error) {
        console.error("Failed to update title:", error);
      }
    }
  };

  const handleCheckboxClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    event.stopPropagation();
    onSelect(avatar._id, true);
  };

  // Eleven Labs voice handling
  const handleElevenLabsVoiceChange = async (voiceId: string) => {
    const selectedVoice = elevenLabsVoices.find(
      (voice) => voice.voice_id === voiceId
    );

    if (selectedVoice) {
      try {
        // Persist to database
        const updatedAvatar = await updateCustomAvatarElevenLabsVoice(
          avatar._id,
          {
            elevenLabsVoiceId: voiceId,
            voiceName: selectedVoice.name,
            voiceConfiguration: elevenLabsVoiceSettings,
          }
        );

        // Update local state with the response from backend
        setAvatarLabActors((prev) =>
          prev.map((a) => (a._id === avatar._id ? updatedAvatar : a))
        );

        setSelectedElevenLabsVoiceId(voiceId);

        // Play preview if available
        if (selectedVoice.preview_url) {
          audioRef.current!.src = selectedVoice.preview_url;
          audioRef
            .current!.play()
            .catch((e) => console.error("Preview play error:", e));
        }
      } catch (error) {
        console.error("Error updating Eleven Labs voice:", error);
        addNotification("Failed to update Eleven Labs voice", "error");
      }
    }
  };

  const handleElevenLabsSettingChange = (key: string, value: any) => {
    const updatedSettings = { ...elevenLabsVoiceSettings, [key]: value };
    setElevenLabsVoiceSettings(updatedSettings);

    // Update the avatar in state with new settings
    setAvatarLabActors((prev) =>
      prev.map((a) =>
        a._id === avatar._id
          ? {
              ...a,
              voiceConfiguration: updatedSettings,
            }
          : a
      )
    );
  };

  // Heygen voice handling
  const handleHeygenSettingChange = async <K extends keyof TextVoiceSettings>(
    key: K,
    value: TextVoiceSettings[K]
  ) => {
    try {
      if (!avatar.voice_settings || avatar.voice_settings.type !== "text")
        return;

      const currentSettings = avatar.voice_settings as TextVoiceSettings;
      const updatedSettings: TextVoiceSettings = {
        ...currentSettings,
        [key]: value,
      };

      // Update local state
      setAvatarLabActors((prev) =>
        prev.map((a) =>
          a._id === avatar._id ? { ...a, voice_settings: updatedSettings } : a
        )
      );

      // Play preview if available
      if (avatar.heygenVoiceAudioUrl) {
        audioRef.current!.src = avatar.heygenVoiceAudioUrl;
        audioRef
          .current!.play()
          .catch((e) => console.error("Preview play error:", e));
      }
    } catch (error) {
      console.error("Error updating Heygen voice settings:", error);
      addNotification("Failed to update voice settings", "error");
    }
  };

  const handleAddHeygenVoice = async (
    selectedId: string,
    meta?: { name: string; preview_audio_url: string | null }
  ) => {
    if (!currentWorkspace || !selectedId) return;

    try {
      const libraryMatch = heygenVoices.find((v) => v.voice_id === selectedId);
      const customMatch = heygenCustomVoices.find((v) => v.voice_id === selectedId);
      const selectedVoice = libraryMatch ||
        (customMatch
          ? {
              voice_id: customMatch.voice_id,
              name: customMatch.name,
              preview_audio_url: customMatch.preview_audio_url,
            }
          : meta
            ? { voice_id: selectedId, name: meta.name, preview_audio_url: meta.preview_audio_url }
            : null);

      if (!selectedVoice) return;

      // Create new voice settings
      const newSettings: TextVoiceSettings = {
        type: "text",
        voice_id: selectedId,
        input_text: "",
        speed: 1.0,
        pitch: 0,
      };

      // Update local state with Heygen voice
      setAvatarLabActors((prev) =>
        prev.map((a) =>
          a._id === avatar._id
            ? {
                ...a,
                heygenVoiceId: selectedId,
                heygenVoiceAudioUrl: selectedVoice.preview_audio_url ?? undefined,
                voice_settings: newSettings,
                // Clear Eleven Labs voice when switching to Heygen
                elevenLabsVoiceId: undefined,
                voiceConfiguration: undefined,
              }
            : a
        )
      );

      setSelectedHeygenVoiceId(selectedId);

      // Play preview
      if (selectedVoice.preview_audio_url) {
        audioRef.current!.src = selectedVoice.preview_audio_url;
        audioRef
          .current!.play()
          .catch((e) => console.error("Preview play error:", e));
      }
    } catch (error) {
      console.error("Error adding Heygen voice:", error);
      addNotification("Failed to add Heygen voice", "error");
    }
  };

  const playAudio = (url: string) => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }

    const audio = new Audio(url);
    currentAudioRef.current = audio;
    audio.play().catch((error) => {
      console.error("Error playing audio:", error);
    });
  };

  const stopAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
  };

  const handleHearPreview = async () => {
    if (!selectedHeygenVoiceId) {
      addNotification("Please select a voice first", "error");
      return;
    }

    const scriptText = [
      currentScript?.headline || "",
      currentScript?.body || "",
      currentScript?.cta || "",
    ]
      .filter(Boolean)
      .join("\n");

    if (!scriptText.trim()) {
      addNotification("Please write a script first to hear the audio preview", "error");
      return;
    }

    try {
      setIsLoadingPreview(true);
      const voiceSettings = avatar.voice_settings as TextVoiceSettings | undefined;
      const response = await generateVoicePreview({
        voice_id: selectedHeygenVoiceId,
        text: scriptText,
        settings: {
          pitch: String(voiceSettings?.pitch ?? 0),
          speed: String(voiceSettings?.speed ?? 1),
        },
      });

      if (response.data?.audio_url) {
        playAudio(response.data.audio_url);
      }
    } catch (error: any) {
      // HeyGen's /v3/voices/speech only supports voices on the starfish engine.
      // For voices that don't (custom designed voices, some library voices),
      // fall back to the voice's static preview_audio_url.
      const message: string =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "";
      const isStarfishUnsupported = /starfish/i.test(message) && /not supported/i.test(message);
      if (isStarfishUnsupported) {
        const fallbackUrl =
          heygenCustomVoices.find((v) => v.voice_id === selectedHeygenVoiceId)?.preview_audio_url ||
          heygenVoices.find((v) => v.voice_id === selectedHeygenVoiceId)?.preview_audio_url ||
          null;
        if (fallbackUrl) {
          playAudio(fallbackUrl);
          addNotification(
            "This voice doesn't support text-to-speech preview. Playing the voice's original sample instead.",
            "info"
          );
        } else {
          addNotification(
            "This voice doesn't support text-to-speech preview and has no sample audio.",
            "error"
          );
        }
      } else {
        console.error("Error generating voice preview:", error);
        addNotification(
          message || "Failed to generate voice preview",
          "error"
        );
      }
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const fetchHeygenCustomVoices = async () => {
    if (!currentWorkspace?._id) return;
    try {
      setHeygenCustomVoicesLoading(true);
      const response = await getCustomVoicesByWorkspace(currentWorkspace._id);
      setHeygenCustomVoices(response.data || []);
    } catch (error) {
      console.error("Error fetching custom Heygen voices:", error);
    } finally {
      setHeygenCustomVoicesLoading(false);
    }
  };

  useEffect(() => {
    fetchHeygenCustomVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?._id]);

  useEffect(() => {
    const fetchHeygenVoices = async () => {
      try {
        const response = await listAllHeygenVoices();
        setHeygenVoices(response.data);
      } catch (error) {
        console.error("Error fetching Heygen voices:", error);
      }
    };

    const fetchElevenLabsVoices = async () => {
      if (!user?.email) return;
      try {
        const response = await elevenLabsFF();
        const isEnabled = response === "true";
        setElevenLabsEnabled(isEnabled);
        if (isEnabled) {
          const voices = await getElevenLabsVoices(user.email);
          setElevenLabsVoices(voices);
        }
      } catch (error) {
        console.error("Error fetching Eleven Labs voices:", error);
      }
    };

    fetchHeygenVoices();
    fetchElevenLabsVoices();
  }, [user]);

  useEffect(() => {
    if (audioRef.current) {
      // Set audio source based on current voice selection
      if (voiceSelection === "elevenLabs" && selectedElevenLabsVoiceId) {
        const voice = elevenLabsVoices.find(
          (v) => v.voice_id === selectedElevenLabsVoiceId
        );
        if (voice?.preview_url) {
          audioRef.current.src = voice.preview_url;
        }
      } else if (voiceSelection === "heygen" && avatar.heygenVoiceAudioUrl) {
        audioRef.current.src = avatar.heygenVoiceAudioUrl;
      }
    }
  }, [
    avatar.heygenVoiceAudioUrl,
    selectedElevenLabsVoiceId,
    voiceSelection,
    elevenLabsVoices,
  ]);

  return (
    <div style={{ position: "relative" }}>
      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {isEditingTitle ? (
              <TextField
                value={currentTitle}
                onChange={handleTitleChange}
                onBlur={handleTitleSave}
                onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
                autoFocus
                size="small"
                sx={{
                  ml: 1,
                  "& .MuiInputBase-root": {
                    height: 32,
                    fontSize: "0.875rem",
                  },
                  width: "30%",
                }}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  ml: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: currentTitle
                    ? THEME_COLORS.darkYellow
                    : THEME_COLORS.darkGray,
                  padding: "2px 8px",
                  borderRadius: "10px",
                  width: "35%",
                }}
                onClick={() => setIsEditingTitle(true)}
              >
                {currentTitle || "No title"}
                <EditIcon fontSize="small" sx={{ ml: 0.5, width: "12px" }} />
              </Typography>
            )}
          </div>
          <div style={{ marginBottom: "6px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Checkbox */}
              <Checkbox
                checked={isSelected}
                onChange={handleCheckboxClick}
                className={classes.checkbox}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />

              {/* Avatar Thumbnail */}
              <img
                src={
                  avatar?.selected_image_url ||
                  avatar?.image_url_list?.[0] ||
                  "/placeholder-avatar.png"
                }
                alt={avatar.name}
                className={classes.avatarThumbnail}
                style={{ width: "80px", height: "80px", objectFit: "cover" }}
              />

              {/* Play Button */}
              <button onClick={togglePlay} className={classes.playButton}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </button>

              {/* Sound Wave */}
              <div className={classes.soundWave} />

              <div>
                <div style={{ display: "flex" }}>
                  {/* Voice Settings Button */}
                  <Tooltip title="Voice Settings">
                    <IconButton
                      onClick={handleVoiceSettings}
                      style={{
                        color: THEME_COLORS.yellow,
                        background: "none",
                        border: "none",
                        fontSize: "11px",
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                    >
                      <SettingsVoiceIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Switch to Photo Avatar Button - show when photo avatar version exists */}
                  {hasPhotoAvatarVersion && onSwitchToPhotoAvatar && (
                    <Tooltip title="Switch to Photo Avatar mode (enables templates, backgrounds, and more)">
                      <IconButton
                        onClick={handleSwitchToPhotoAvatar}
                        style={{
                          color: THEME_COLORS.yellow,
                          background: "none",
                          border: "none",
                          fontSize: "11px",
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        <AutoFixHighIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Settings Panel */}
      {showSettings && (
        <div className={classes.voiceSettingsPanel}>
          <span style={{ fontSize: "11px", color: THEME_COLORS.darkGray }}>
            Choose voice from...
          </span>

          <div style={{ display: "flex" }}>
            <RadioGroup
              value={voiceSelection}
              onChange={(e) =>
                setVoiceSelection(e.target.value as "heygen" | "elevenLabs")
              }
              row
            >
              <FormControlLabel
                value="heygen"
                control={<Radio className={classes.radio} />}
                label={<span className={classes.label}>Voice Library</span>}
              />
              <FormControlLabel
                value="elevenLabs"
                className={classes.radio}
                control={<Radio className={classes.radio} />}
                label={
                  <span className={classes.label}>
                    Eleven Labs{" "}
                    {elevenLabsEnabled ? (
                      ""
                    ) : (
                      <span
                        style={{
                          fontSize: "11px",
                          color: THEME_COLORS.darkGray,
                        }}
                      >
                        (unavailable)
                      </span>
                    )}{" "}
                  </span>
                }
                disabled={!elevenLabsEnabled}
              />
            </RadioGroup>
          </div>

          {/* Heygen Voice Settings */}
          {voiceSelection === "heygen" && (
            <>
              <div className={classes.settingsRow}>
                <span>Voice: </span>
                <button
                  className={classes.voiceButton}
                  onClick={(e) => {
                    e.preventDefault();
                    setHeygenVoiceModalOpen(true);
                  }}
                >
                  {selectedHeygenVoiceId
                    ? heygenVoices.find(
                        (voice) => voice.voice_id === selectedHeygenVoiceId
                      )?.name ||
                      heygenCustomVoices.find(
                        (voice) => voice.voice_id === selectedHeygenVoiceId
                      )?.name ||
                      "Select Voice"
                    : "Select Voice"}
                </button>

                <VoiceSelectionModal
                  open={heygenVoiceModalOpen}
                  onClose={() => setHeygenVoiceModalOpen(false)}
                  heygenVoices={heygenVoices}
                  onSelectVoice={async (voiceId, meta) =>
                    await handleAddHeygenVoice(voiceId, meta)
                  }
                  actorId={avatar._id}
                  actorName={avatar.name}
                  customVoices={heygenCustomVoices}
                  customVoicesLoading={heygenCustomVoicesLoading}
                  onRefreshCustomVoices={fetchHeygenCustomVoices}
                />
              </div>

              {/* Heygen Voice Settings */}
              {selectedHeygenVoiceId &&
                avatar.voice_settings?.type === "text" && (
                  <div className={classes.settings}>
                    <div className={classes.settingsRow}>
                      <span>Speed</span>
                      <Slider
                        value={
                          (avatar.voice_settings as TextVoiceSettings).speed ||
                          1.0
                        }
                        onChange={(e, value) =>
                          handleHeygenSettingChange("speed", value as number)
                        }
                        min={0.5}
                        max={1.5}
                        step={0.1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                      />
                    </div>

                    <div className={classes.settingsRow}>
                      <span>Pitch</span>
                      <Slider
                        value={
                          (avatar.voice_settings as TextVoiceSettings).pitch ||
                          0
                        }
                        onChange={(e, value) =>
                          handleHeygenSettingChange("pitch", value as number)
                        }
                        min={-50}
                        max={50}
                        step={1}
                        valueLabelDisplay="auto"
                      />
                    </div>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleHearPreview}
                      disabled={isLoadingPreview || !selectedHeygenVoiceId}
                      startIcon={
                        isLoadingPreview ? (
                          <CircularProgress size={16} />
                        ) : (
                          <VolumeUpIcon fontSize="small" />
                        )
                      }
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontSize: "12px",
                        borderColor: THEME_COLORS.yellow,
                        color: THEME_COLORS.darkYellow,
                        "&:hover": {
                          borderColor: THEME_COLORS.darkYellow,
                          backgroundColor: "rgba(255, 191, 0, 0.1)",
                        },
                        "&.Mui-disabled": {
                          borderColor: "#ccc",
                          color: "#999",
                        },
                      }}
                    >
                      {isLoadingPreview
                        ? "Generating preview..."
                        : "Hear Audio Preview"}
                    </Button>
                  </div>
                )}
            </>
          )}

          {/* Eleven Labs Voice Settings */}
          {voiceSelection === "elevenLabs" && (
            <>
              <div className={classes.settingsRow}>
                <span>Voice: </span>
                <button
                  className={classes.voiceButton}
                  onClick={(e) => {
                    e.preventDefault();
                    setElevenLabsVoiceModalOpen(true);
                  }}
                >
                  {selectedElevenLabsVoiceId
                    ? elevenLabsVoices.find(
                        (voice) => voice.voice_id === selectedElevenLabsVoiceId
                      )?.name
                    : "Select Voice"}
                </button>

                <ElevenLabsVoicesDialog
                  open={elevenLabsVoiceModalOpen}
                  onClose={() => setElevenLabsVoiceModalOpen(false)}
                  elevenLabsVoices={elevenLabsVoices}
                  onSelectVoice={async (voiceId) =>
                    await handleElevenLabsVoiceChange(voiceId)
                  }
                />
              </div>

              {/* Eleven Labs Settings */}
              {selectedElevenLabsVoiceId && (
                <div className={classes.settings}>
                  <div className={classes.settingsRow}>
                    <span>Stability</span>
                    <Slider
                      value={elevenLabsVoiceSettings.stability}
                      onChange={(e, value) =>
                        handleElevenLabsSettingChange("stability", value)
                      }
                      min={0}
                      max={1}
                      step={0.05}
                    />
                  </div>
                  <div className={classes.settingsRow}>
                    <span>Similarity</span>
                    <Slider
                      value={elevenLabsVoiceSettings.similarity_boost}
                      onChange={(e, value) =>
                        handleElevenLabsSettingChange("similarity_boost", value)
                      }
                      min={0}
                      max={1}
                      step={0.05}
                    />
                  </div>
                  <div className={classes.settingsRow}>
                    <span>Style</span>
                    <Slider
                      value={elevenLabsVoiceSettings.style}
                      onChange={(e, value) =>
                        handleElevenLabsSettingChange("style", value)
                      }
                      min={0}
                      max={1}
                      step={0.05}
                    />
                  </div>
                  <div className={classes.settingsRow}>
                    <span>Speaker Boost</span>
                    <Switch
                      checked={elevenLabsVoiceSettings.use_speaker_boost}
                      onChange={(e) =>
                        handleElevenLabsSettingChange(
                          "use_speaker_boost",
                          e.target.checked
                        )
                      }
                    />
                  </div>
                  <div className={classes.settingsRow}>
                    <span>Speed</span>
                    <Slider
                      value={elevenLabsVoiceSettings.speed || 1.0}
                      onChange={(e, value) =>
                        handleElevenLabsSettingChange("speed", value)
                      }
                      min={0.7}
                      max={1.2}
                      step={0.1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Audio Element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        style={{ display: "none" }}
      />

      {/* Voice Required Message */}
      {!avatar.heygenVoiceId && !avatar.elevenLabsVoiceId && (
        <div
          style={{
            marginTop: "8px",
            padding: "4px 8px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "4px",
            fontSize: "12px",
            color: "#856404",
            textAlign: "center",
          }}
        >
          This avatar requires a voice selection
        </div>
      )}
    </div>
  );
};

export default AvatarLabVoicePlayer;
